import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  PLATFORM_LABELS,
  EMAIL_NOT_VERIFIED_MESSAGE,
  SUBMISSION_CHECKS,
  captionRulesSentence,
  type CampaignListResponse,
  type Platform,
  type PublicCampaign,
  type SubmissionCreateResponse,
} from '@nexus/shared';

import { useSession } from '../auth/SessionContext';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { reviewPromise, usePublicConfig } from '../lib/publicConfig';
import { CheckList } from '../components/CheckList';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ExternalLink,
  LoadingPage,
  PageHeader,
  SelectField,
  TextField,
  statusTone,
} from '../ui';

/**
 * Submit a clip: pick a campaign you have joined, paste the link, watch the
 * checks.
 *
 * ONLY JOINED CAMPAIGNS ARE OFFERED, because the API refuses anything else and
 * a dropdown that lets a creator pick a campaign they will then be refused
 * from is a form designed to waste their time. A creator who has joined
 * nothing is pointed at the Campaigns page instead of being shown an empty
 * select.
 *
 * THE CHECKS ARE SHOWN IN ORDER, INCLUDING THE ONES THAT DID NOT RUN. The plan
 * asks for "the checks running in order and where it stopped", and a list that
 * only contains what happened cannot show where it stopped: the checks after
 * the failure are exactly the information the creator needs to understand that
 * nothing else was wrong.
 *
 * The campaign's rules sit BESIDE the form, not under it. Somebody pasting a
 * link is about to be judged against those words; making them scroll to read
 * them is how a hold happens.
 */
export function Submit(): React.JSX.Element {
  const { session } = useSession();
  /* The API refuses a submission from an unconfirmed address with the same
     sentence; saying it before the round trip saves pasting a link into a
     form that cannot take it. */
  const emailVerified = session?.user.emailVerified !== false;
  const [campaigns, setCampaigns] = useState<PublicCampaign[] | null>(null);
  const [campaignId, setCampaignId] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmissionCreateResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  /*
   * A phone is why this exists.
   *
   * The result lands BELOW the form and the campaign's rules, which on a 390px
   * screen is a screen and a half further down: somebody pressed Submit,
   * nothing they can see changed, and the natural next move is to press it
   * again. Moving the keyboard to the result both scrolls it into view and
   * puts a screen reader inside it, and `aria-live` covers the case where
   * focus was already there.
   */
  useEffect(() => {
    if (!result) return;
    resultRef.current?.focus();
    /* Optional-called: jsdom has no layout, so it has no `scrollIntoView`, and
       a missing scroll must never take the result down with it. */
    resultRef.current?.scrollIntoView?.({
      block: 'start',
      /* Reduced motion is a setting about motion, and a smooth scroll is
         motion. The stylesheet honours it for animations; this is the one
         piece of movement the stylesheet cannot reach. */
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  }, [result]);

  const load = useCallback(async () => {
    try {
      const body = await apiGet<CampaignListResponse>('/campaigns');
      setCampaigns(body.campaigns);
    } catch {
      setError('Could not load your campaigns.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const joined = useMemo(
    () => (campaigns ?? []).filter((c) => c.participation?.status === 'JOINED'),
    [campaigns],
  );

  const selected = joined.find((c) => c.id === campaignId) ?? joined[0] ?? null;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const body = await apiSend<SubmissionCreateResponse>('POST', '/submissions', {
        campaignId: selected.id,
        url: url.trim(),
      });
      setResult(body);
      setUrl('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  if (campaigns === null && error === null) {
    return <LoadingPage eyebrow="Creator" title="Submit a clip" />;
  }

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Submit a clip"
        lead="Post the clip on your own account first, then paste the link. The checks run in order and stop at the first problem."
      />

      {!emailVerified && (
        <Alert tone="waiting" title="Confirm your email first">
          {EMAIL_NOT_VERIFIED_MESSAGE}
        </Alert>
      )}

      {joined.length === 0 ? (
        <Card>
          <EmptyState
            title="You have not joined a campaign yet."
            action={
              <Link className="btn btn-primary" to="/campaigns">
                Find a campaign
              </Link>
            }
          >
            Read a campaign's rules and join it, then cut your clip, post it on your own account
            and paste the link here.
          </EmptyState>
        </Card>
      ) : (
        <div className="split reference">
          {/* `reference` and not the default 1.55fr/1fr: this is a form beside
              the rules you check it against, and both are content widths. The
              fraction gave the form a 1,357px card at 2,560 to hold a 480px
              field in. */}
          <Card variant="feature">
            <form className="form" onSubmit={(e) => void submit(e)}>
              <SelectField
                label="Campaign"
                id="campaign"
                value={selected?.id ?? ''}
                onChange={(e) => setCampaignId(e.target.value)}
                hint="Only the campaigns you have joined. Joining is on the Campaigns page."
              >
                {joined.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} · {campaign.brand.displayName}
                  </option>
                ))}
              </SelectField>

              <TextField
                label="Link to your post"
                id="url"
                name="url"
                type="text"
                inputMode="url"
                autoComplete="off"
                placeholder="https://www.tiktok.com/@you/video/…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                hint="TikTok, Instagram or YouTube. A share link works too — we follow it."
                {...(error ? { 'aria-invalid': true } : {})}
              />

              <Button
                type="submit"
                variant="primary"
                disabled={busy || url.trim().length === 0 || !emailVerified}
              >
                {busy ? 'Running the checks…' : 'Submit'}
              </Button>
            </form>

            {error && <Alert tone="critical">{error}</Alert>}
          </Card>

          {selected && (
            <Card
              label="What this campaign asks for"
              title={`What ${selected.name} asks for`}
              headingLevel={2}
              aside={
                <>
                  {selected.allowedPlatforms.map((p) => (
                    <Badge key={p} plain>
                      {PLATFORM_LABELS[p as Platform] ?? p}
                    </Badge>
                  ))}
                </>
              }
            >
              {selected.contentRules && <pre className="prose-block">{selected.contentRules}</pre>}
              <p className="muted">Caption: {captionRulesSentence(selected.captionRules)}</p>
              {selected.sourceLinks.length > 0 && (
                <p className="muted">
                  Cut from{' '}
                  {selected.sourceLinks.map((link, index) => (
                    <span key={link.url}>
                      {index > 0 && ', '}
                      <ExternalLink href={link.url}>{link.label}</ExternalLink>
                    </span>
                  ))}
                  .
                </p>
              )}
            </Card>
          )}
        </div>
      )}

      <div
        ref={resultRef}
        tabIndex={-1}
        aria-live="polite"
        aria-atomic="false"
        className="stack"
        data-scroll-anchor=""
      >
        {result && <SubmissionResult result={result} />}
      </div>
    </>
  );
}

function SubmissionResult({ result }: { result: SubmissionCreateResponse }): React.JSX.Element {
  const { submission, stoppedAt } = result;
  const config = usePublicConfig();
  const inReview = submission.status === 'IN_REVIEW';
  const held = submission.status === 'HELD';
  const rejected = submission.status === 'REJECTED';

  return (
    <Card
      label="Checks"
      eyebrow={PLATFORM_LABELS[submission.platform as Platform] ?? submission.platform}
      title={submission.statusLabel}
      variant={rejected ? 'dimmed' : 'feature'}
      aside={
        /* The tone comes from the system, not from this screen, so a hold is
           the same colour here as on My submissions and in the queue. */
        <Badge tone={statusTone(submission.status, 'submission')}>
          {rejected ? 'Stopped' : held ? 'Waiting on us' : 'In review'}
        </Badge>
      }
    >
      {inReview && (
        <Alert tone="positive" title="Every automatic check passed.">
          <span>
            It is in content review now — an operator confirms it follows the campaign rules.
            We review <span data-testid="review-promise">{reviewPromise(config?.reviewSlaHours)}</span>.
            You do not need to do anything.
          </span>
        </Alert>
      )}
      {held && <Alert tone="waiting">{submission.holdReason}</Alert>}
      {rejected && <Alert tone="critical">{submission.rejectionReason}</Alert>}

      <CheckList checks={submission.checks} stoppedAt={stoppedAt?.planName ?? null} />

      <p className="muted">
        <ExternalLink href={submission.postUrl} />
      </p>
      <p className="muted">
        {SUBMISSION_CHECKS.length} checks run in order, and the run stops at the first failure.{' '}
        <Link to="/submissions">See all your submissions</Link>.
      </p>
    </Card>
  );
}
